import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { isIOS } from "react-device-detect";
import ImageSlider from "../../components/ImageSlider";
import TypingMain from "../../components/TypingMain";
import { getDate, getGanjiToday, isEmbedMode, requestParentResize, scrollToTopForEmbed } from "../../util";
import { useGPTStore } from "../../store/useGPT";
import { useFortuneLabStore, SolarType } from "../../store/useFortuneLab";
import FortuneLabPrivacyPolicyModal from "../../components/FortuneLabPrivacyPolicyModal";
import BusinessInfoFooter from "../../components/BusinessInfoFooter";

interface FormState {
  name: string;
  phoneNumber: string;
  birthDate: string;
  solarType: SolarType;
}

interface FormErrors {
  name?: string;
  phoneNumber?: string;
  birthDate?: string;
  agreement?: string;
}

const MAX_NAME_LENGTH = 20;
const MAX_PHONE_LENGTH = 11;

const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);

const FORTUNE_LAB_BASE_URL =
  process.env.REACT_APP_FORTUNE_LAB_BASE_URL || process.env.REACT_APP_BASE_URL || "";

const isValidKoreanPhoneNumber = (value: string) => {
  if (!/^01[016789]\d{7,8}$/.test(value)) {
    return false;
  }

  const subscriber = value.slice(3);
  if (/^(\d)\1+$/.test(subscriber)) {
    return false;
  }

  if (isSequentialDigits(subscriber)) {
    return false;
  }

  return true;
};

const formatBirthDateInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);

  const parts = [] as string[];
  if (digits.length === 0) {
    return "";
  }
  parts.push(digits.slice(0, Math.min(4, digits.length)));
  if (digits.length > 4) {
    parts.push(digits.slice(4, Math.min(6, digits.length)));
  }
  if (digits.length > 6) {
    parts.push(digits.slice(6, 8));
  }

  return parts.filter(Boolean).join(".");
};

const getBirthDateDigits = (value: string) => value.replace(/\D/g, "");

const isValidBirthDate = (value: string) => {
  if (value.length !== 8) {
    return false;
  }

  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(4, 6));
  const day = Number(value.slice(6, 8));

  if (Number.isNaN(year) || Number.isNaN(month) || Number.isNaN(day)) {
    return false;
  }

  if (month < 1 || month > 12) {
    return false;
  }

  const constructedDate = new Date(year, month - 1, day);
  if (
    constructedDate.getFullYear() !== year ||
    constructedDate.getMonth() + 1 !== month ||
    constructedDate.getDate() !== day
  ) {
    return false;
  }

  const today = new Date();
  if (constructedDate > today) {
    return false;
  }

  return year >= 1900;
};

const normalizeBirthDate = (value: string) => {
  const digits = getBirthDateDigits(value);
  if (digits.length !== 8) {
    return "";
  }

  const year = digits.slice(0, 4);
  const month = digits.slice(4, 6);
  const day = digits.slice(6, 8);

  return `${year}-${month}-${day}`;
};

const isSequentialDigits = (value: string) => {
  if (value.length <= 1) {
    return false;
  }

  let isAscending = true;
  let isDescending = true;

  for (let i = 1; i < value.length; i += 1) {
    const current = Number(value[i]);
    const previous = Number(value[i - 1]);

    if (current - previous !== 1) {
      isAscending = false;
    }

    if (current - previous !== -1) {
      isDescending = false;
    }

    if (!isAscending && !isDescending) {
      return false;
    }
  }

  return isAscending || isDescending;
};

const FortuneLabMainPage = () => {
  const navigate = useNavigate();
  const { trkId: trkIdFromPath } = useParams<{ trkId?: string }>();
  const [searchParams] = useSearchParams();
  const trkIdFromQuery = searchParams.get("trkId") || "";
  const resolvedTrkId = (trkIdFromPath ?? trkIdFromQuery).trim();
  const onlyFormMode = searchParams.get("onlyform") === "1";
  const { trackEvent } = useGPTStore();
  const { registerUser, registerLoading, registeredUser, error, reset, trackLanding } =
    useFortuneLabStore();

  const [companyName, setCompanyName] = useState<string | null>(null);
  const [contactName, setContactName] = useState<string | null>(null);
  const [contactTel, setContactTel] = useState<string | null>(null);
  const [contactEmail, setContactEmail] = useState<string | null>(null);
  const [companyNumber, setCompanyNumber] = useState<string | null>(null);
  const [mailOrderNumber, setMailOrderNumber] = useState<string | null>(null);
  const [bizProfileName, setBizProfileName] = useState<string | null>(null);
  const [landingImageUrls, setLandingImageUrls] = useState<string[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isPrivacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    name: "",
    phoneNumber: "",
    birthDate: "",
    solarType: "SOLAR",
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isPrivacyAgreementChecked, setPrivacyAgreementChecked] = useState(false);

  useEffect(() => {
    document.title = "오늘의 운세";
  }, []);

  useEffect(() => {
    reset();
  }, [reset]);

  useEffect(() => {
    if (!isEmbedMode()) {
      return;
    }
    requestParentResize([0, 400, 800]);
  }, []);

  useEffect(() => {
    if (!resolvedTrkId) {
      localStorage.removeItem("trkId");
      return;
    }

    const landingKey = `fortuneLabLanding:${resolvedTrkId}`;
    if (!localStorage.getItem(landingKey)) {
      trackLanding(resolvedTrkId);
      localStorage.setItem(landingKey, "true");
    }

    localStorage.setItem("trkId", resolvedTrkId);
    trackEvent(resolvedTrkId);
  }, [resolvedTrkId, trackEvent, trackLanding]);

  useEffect(() => {
    if (!resolvedTrkId) {
      setCompanyName(null);
      setContactName(null);
      setContactTel(null);
      setContactEmail(null);
      setLandingImageUrls([]);
      setMailOrderNumber(null);
      return;
    }

    if (!FORTUNE_LAB_BASE_URL) {
      return;
    }

    let isCancelled = false;

    const fetchTrackingInfo = async () => {
      try {
        const response = await fetch(
          `${FORTUNE_LAB_BASE_URL}/fortune-lab/tracking/info?trkId=${resolvedTrkId}`
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (isCancelled) {
          return;
        }

        const info = data?.contents ?? {};

        const fetchedCompanyName = info?.companyName;
        const fetchedContactName = info?.ceoName;
        const fetchedContactTel = info?.contactTel;
        const fetchedContactEmail = info?.contactEmail;
        const fetchedLandingImages = Array.isArray(info?.landingImageUrls)
          ? info.landingImageUrls.filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0)
          : [];
        const fallbackLandingGif = typeof (info as { landingGifUrl?: string } | undefined)?.landingGifUrl === 'string'
          ? (info as { landingGifUrl?: string }).landingGifUrl
          : null;

        setCompanyName(
          typeof fetchedCompanyName === "string" && fetchedCompanyName.trim().length > 0
            ? fetchedCompanyName.trim()
            : null
        );
        setContactName(
          typeof fetchedContactName === "string" && fetchedContactName.trim().length > 0
            ? fetchedContactName.trim()
            : null
        );
        setContactTel(
          typeof fetchedContactTel === "string" && fetchedContactTel.trim().length > 0
            ? fetchedContactTel.trim()
            : null
        );
        setContactEmail(
          typeof fetchedContactEmail === "string" && fetchedContactEmail.trim().length > 0
            ? fetchedContactEmail.trim()
            : null
        );
        if (fetchedLandingImages.length > 0) {
          setLandingImageUrls(fetchedLandingImages.map((url: string) => url.trim()));
        } else if (fallbackLandingGif && fallbackLandingGif.trim().length > 0) {
          setLandingImageUrls([fallbackLandingGif.trim()]);
        } else {
          setLandingImageUrls([]);
        }

        // 사업자 정보 설정
        setCompanyNumber(
          typeof info?.companyNumber === "string" && info.companyNumber.trim().length > 0
            ? info.companyNumber.trim()
            : null
        );
        setMailOrderNumber(
          typeof info?.mailOrderNumber === "string" && info.mailOrderNumber.trim().length > 0
            ? info.mailOrderNumber.trim()
            : null
        );
        setBizProfileName(
          typeof info?.bizProfileName === "string" && info.bizProfileName.trim().length > 0
            ? info.bizProfileName.trim()
            : null
        );
      } catch (fetchError) {
        console.error("fortune-lab tracking info error", fetchError);
        if (!isCancelled) {
          setCompanyName(null);
          setContactName(null);
          setContactTel(null);
          setContactEmail(null);
          setCompanyNumber(null);
          setMailOrderNumber(null);
          setBizProfileName(null);
          setLandingImageUrls([]);
        }
      }
    };

    fetchTrackingInfo();

    return () => {
      isCancelled = true;
    };
  }, [resolvedTrkId]);

  useEffect(() => {
    if (!isEmbedMode()) {
      return;
    }
    requestParentResize([0, 400, 800]);
  }, [landingImageUrls]);

  // 이미지 프리로드
  useEffect(() => {
    if (landingImageUrls.length === 0) {
      setImagesLoaded(true);
      return;
    }

    let loadedCount = 0;
    landingImageUrls.forEach((url) => {
      const img = new Image();
      img.onload = img.onerror = () => {
        loadedCount++;
        if (loadedCount === landingImageUrls.length) {
          setImagesLoaded(true);
        }
      };
      img.src = url;
    });
  }, [landingImageUrls]);

  useEffect(() => {
    const updateMainStyle = () => {
      const mainElement = document.querySelector(".main_container main");
      if (mainElement instanceof HTMLElement) {
        const isKakaoOS = navigator.userAgent.toLowerCase().includes("kakao");
        let additionalPadding = 0;

        if (isIOS && !isKakaoOS) {
          additionalPadding = 100;
        } else if (isIOS && isKakaoOS) {
          additionalPadding = 40;
        }

        mainElement.style.paddingBottom = `${additionalPadding}px`;
      }
    };

    updateMainStyle();
    window.addEventListener("resize", updateMainStyle);
    return () => window.removeEventListener("resize", updateMainStyle);
  }, []);

  useEffect(() => {
    if (registeredUser) {
      if (isEmbedMode()) {
        scrollToTopForEmbed("smooth");
      }
      navigate("/fortune-lab/loading");
    }
  }, [registeredUser, navigate]);

  useEffect(() => {
    if (isPrivacyModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isPrivacyModalOpen]);

  useEffect(() => {
    if (!isEmbedMode()) {
      return;
    }

    const handleParentScrollMessage = (event: MessageEvent) => {
      if (!event || typeof event.data !== "object" || event.data === null) {
        return;
      }

      const { type, source } = event.data as { type?: string; source?: string };
      if ((type === "SAJU_EMBED_SCROLL_TOP" && source === "today-saju-parent") ||
          (type === "SAJU_PRIVACY_RETURN" && source === "today-saju-privacy")) {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
        scrollToTopForEmbed("smooth");
      }
    };

    window.addEventListener("message", handleParentScrollMessage);
    return () => window.removeEventListener("message", handleParentScrollMessage);
  }, []);

  const handleChange = (field: keyof FormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;

      let nextValue: string;
      if (field === "name") {
        nextValue = rawValue.slice(0, MAX_NAME_LENGTH);
      } else if (field === "phoneNumber") {
        nextValue = sanitizePhoneNumber(rawValue);
      } else if (field === "birthDate") {
        nextValue = formatBirthDateInput(rawValue);
      } else {
        nextValue = rawValue;
      }

      setFormState((prev) => ({ ...prev, [field]: nextValue }));
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    };

  const validate = () => {
    const nextErrors: FormErrors = {};

    const trimmedName = formState.name.trim();
    if (!trimmedName) {
      nextErrors.name = "이름을 입력해주세요.";
    } else if (trimmedName.length > MAX_NAME_LENGTH) {
      nextErrors.name = "이름은 20자 이하로 입력해주세요.";
    }

    const phone = sanitizePhoneNumber(formState.phoneNumber);
    if (!phone) {
      nextErrors.phoneNumber = "전화번호를 입력해주세요.";
    } else if (!isValidKoreanPhoneNumber(phone)) {
      nextErrors.phoneNumber = "유효한 휴대전화번호를 입력해주세요.";
    }

    const birthDateDigits = getBirthDateDigits(formState.birthDate);
    if (!birthDateDigits) {
      nextErrors.birthDate = "생년월일을 입력해주세요.";
    } else if (!isValidBirthDate(birthDateDigits)) {
      nextErrors.birthDate = "생년월일 형식을 다시 확인해주세요.";
    }

    if (!isPrivacyAgreementChecked) {
      nextErrors.agreement = "개인정보 수집·이용에 동의해주세요.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (registerLoading) return;

    if (!validate()) {
      return;
    }

    const trkId = localStorage.getItem("trkId") || "";
    const sanitizedPhoneNumber = sanitizePhoneNumber(formState.phoneNumber);
    const normalizedBirthDate = normalizeBirthDate(formState.birthDate);

    if (!normalizedBirthDate) {
      setFormErrors((prev) => ({
        ...prev,
        birthDate: "생년월일 형식을 다시 확인해주세요.",
      }));
      return;
    }

    await registerUser({
      name: formState.name.trim(),
      phoneNumber: sanitizedPhoneNumber,
      birthDate: normalizedBirthDate,
      solarType: formState.solarType,
      trkId,
    });
  };

  const handlePrivacyLinkClick = () => {
    if (isEmbedMode()) {
      const nextParams = new URLSearchParams(searchParams);
      if (resolvedTrkId) {
        nextParams.set("trkId", resolvedTrkId);
      } else {
        nextParams.delete("trkId");
      }
      const search = nextParams.toString();
      navigate(`/fortune-lab/privacy-policy${search ? `?${search}` : ""}`);
      return;
    }

    setPrivacyModalOpen(true);
  };

  if (!imagesLoaded) {
    return (
      <div className="main_container">
        <main style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <p>로딩 중...</p>
        </main>
      </div>
    );
  }

  return (
    <>
      <div className="main_container">
        <main className="landing">
          {!onlyFormMode && (
            <>
              {landingImageUrls.length > 0 ? (
                <>
                  <div
                    style={{
                      width: '100%',
                      marginBottom: '24px',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {landingImageUrls.map((url, index) => (
                      <img
                        key={`${url}-${index}`}
                        src={url}
                        alt={`캠페인 랜딩 ${index + 1}`}
                        style={{ width: '100%' }}
                      />
                    ))}
                  </div>
                  <div className="button_description">
                    <span
                      className="highlight"
                      style={{ color: '#9c2020', fontWeight: 'bold' }}
                    >
                      무료운세 확인하고
                    </span>{' '}
                      쿠폰받기
                  </div>
                </>
              ) : (
                <>
                  <ImageSlider />
                  <div className="date">{getDate().full}</div>
                  <div className="date-hanja">{getGanjiToday()}</div>

                  <div className="title">명리학을 기반으로 분석한</div><br/>
                  <div className="title highlight">오늘의 운세는?</div>

                  <TypingMain />

                  <div className="button_description">
                    <span
                      className="highlight"
                      style={{ color: "#9c2020", fontWeight: "bold" }}
                    >
                      무료운세 확인하고
                    </span>{" "}
                      쿠폰받기
                  </div>
                </>
              )}
            </>
          )}

          <form className="fortune_lab_form" onSubmit={handleSubmit}>
            <input
              type="text"
              placeholder="이름"
              value={formState.name}
              onChange={handleChange("name")}
              autoComplete="name"
              maxLength={MAX_NAME_LENGTH}
            />
            {formErrors.name && (
              <div className="input_error">{formErrors.name}</div>
            )}

            <input
              type="tel"
              placeholder="전화번호 (숫자만 입력)"
              value={formState.phoneNumber}
              onChange={handleChange("phoneNumber")}
              autoComplete="tel"
              inputMode="numeric"
            />
            {formErrors.phoneNumber && (
              <div className="input_error">{formErrors.phoneNumber}</div>
            )}

            <input
              type="text"
          placeholder="생년월일 (yyyy.mm.dd)"
          value={formState.birthDate}
          onChange={handleChange("birthDate")}
          autoComplete="bday"
          inputMode="numeric"
        />
        {formErrors.birthDate && (
          <div className="input_error">{formErrors.birthDate}</div>
        )}

            <select
              value={formState.solarType}
              onChange={(e) =>
                setFormState((prev) => ({
                  ...prev,
                  solarType: e.target.value as SolarType,
                }))
              }
            >
              <option value="SOLAR">양력</option>
              <option value="LUNAR">음력</option>
              <option value="LUNAR_INTER">음력 (윤달)</option>
            </select>

        <div className="privacy_agreement">
          <label className="privacy_agreement_checkbox">
            <input
              type="checkbox"
              checked={isPrivacyAgreementChecked}
              onChange={(event) => setPrivacyAgreementChecked(event.target.checked)}
            />
            <span className="privacy_agreement_text">
              개인정보 수집·이용에 동의합니다
              <button
                type="button"
                className="privacy_link_button privacy_agreement_link"
                onClick={handlePrivacyLinkClick}
              >
                개인정보 처리방침
              </button>
            </span>
          </label>
        </div>
        {formErrors.agreement && (
          <div className="input_error">{formErrors.agreement}</div>
        )}

        <button type="submit" className="kakao_button" disabled={registerLoading} style={{marginBottom: "24px"}}>
          {registerLoading ? "진행 중..." : "3초만에 운세 확인하기"}
        </button>
      </form>

      {error && <div className="input_error">문제가 발생했습니다. 다시 시도해주세요.</div>}

          <BusinessInfoFooter
            companyNumber={companyNumber}
            mailOrderNumber={mailOrderNumber}
            ceoName={contactName}
            bizProfileName={bizProfileName}
            companyName={companyName}
          />
        </main>
      </div>
      <FortuneLabPrivacyPolicyModal
        open={isPrivacyModalOpen}
        onClose={() => setPrivacyModalOpen(false)}
        companyName={companyName}
        contactName={contactName}
        contactTel={contactTel}
        contactEmail={contactEmail}
        useDefaultFallback={resolvedTrkId.length === 0}
      />
    </>
  );
};

export default FortuneLabMainPage;
